import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Refresh,
  Download,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import Plot from 'react-plotly.js';

interface Point3D {
  x: number;
  y: number;
  z: number;
}

interface PointCloudData {
  points: Point3D[];
  metadata: {
    totalPoints: number;
    bounds: {
      x: [number, number];
      y: [number, number];
      z: [number, number];
    };
  };
}

const CloudPointPage: React.FC = () => {
  const [data, setData] = useState<PointCloudData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useDemoData, setUseDemoData] = useState(true);
  
  // Visualization settings
  const [pointSize, setPointSize] = useState(3);
  const [pointOpacity, setPointOpacity] = useState(0.8);
  const [colorScheme, setColorScheme] = useState('viridis');
  const [showGrid, setShowGrid] = useState(true);
  const [showAxes, setShowAxes] = useState(true);
  const [backgroundColor] = useState('#000000');
  
  // Animation settings
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationSpeed] = useState(50);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [maxFrames, setMaxFrames] = useState(0);
  
  // View settings
  const [cameraDistance, setCameraDistance] = useState(5);
  const [autoRotate, setAutoRotate] = useState(false);
  const [rotationSpeed, setRotationSpeed] = useState(0.5);
  
  // Filtering
  const [pointLimit, setPointLimit] = useState(10000);
  const [showFilters, setShowFilters] = useState(false);
  const [xRange, setXRange] = useState<[number, number]>([-2, 2]);
  const [yRange, setYRange] = useState<[number, number]>([0, 3]);
  const [zRange, setZRange] = useState<[number, number]>([-2, 2]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const dataUrl = useDemoData ? '/pointcloud-demo.json' : '/pointcloud.json';
        const response = await fetch(dataUrl);
        if (!response.ok) {
          throw new Error(`Failed to load point cloud data from ${dataUrl}`);
        }
        
        const rawData = await response.json();
        
        // Process the data
        const allPoints: Point3D[] = [];
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        let minZ = Infinity, maxZ = -Infinity;
        
        // Flatten the nested array structure
        rawData.forEach((frame: number[][]) => {
          frame.forEach((point: number[]) => {
            if (point.length === 3) {
              const [x, y, z] = point;
              allPoints.push({ x, y, z });
              
              minX = Math.min(minX, x);
              maxX = Math.max(maxX, x);
              minY = Math.min(minY, y);
              maxY = Math.max(maxY, y);
              minZ = Math.min(minZ, z);
              maxZ = Math.max(maxZ, z);
            }
          });
        });
        
        const processedData: PointCloudData = {
          points: allPoints,
          metadata: {
            totalPoints: allPoints.length,
            bounds: {
              x: [minX, maxX],
              y: [minY, maxY],
              z: [minZ, maxZ]
            }
          }
        };
        
        setData(processedData);
        setMaxFrames(rawData.length);
        
        // Set initial ranges
        setXRange([minX, maxX]);
        setYRange([minY, maxY]);
        setZRange([minZ, maxZ]);
        
        // Adjust point limit for demo data
        if (useDemoData) {
          setPointLimit(Math.min(1000, allPoints.length));
        } else {
          setPointLimit(Math.min(10000, allPoints.length));
        }
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [useDemoData]);

  // Filter points based on current settings
  const filteredPoints = useMemo(() => {
    if (!data) return [];
    
    return data.points
      .filter(point => 
        point.x >= xRange[0] && point.x <= xRange[1] &&
        point.y >= yRange[0] && point.y <= yRange[1] &&
        point.z >= zRange[0] && point.z <= zRange[1]
      )
      .slice(0, pointLimit);
  }, [data, xRange, yRange, zRange, pointLimit]);

  // Prepare plot data
  const plotData = useMemo(() => {
    if (!filteredPoints.length) return [];
    
    const x = filteredPoints.map(p => p.x);
    const y = filteredPoints.map(p => p.y);
    const z = filteredPoints.map(p => p.z);
    
    return [{
      type: 'scatter3d' as const,
      mode: 'markers' as const,
      x,
      y,
      z,
      marker: {
        size: pointSize,
        opacity: pointOpacity,
        color: z, // Color by z-coordinate
        colorscale: colorScheme,
        showscale: true,
        colorbar: {
          title: 'Z Coordinate',
          titleside: 'right'
        }
      },
      hovertemplate: 
        '<b>Point</b><br>' +
        'X: %{x:.3f}<br>' +
        'Y: %{y:.3f}<br>' +
        'Z: %{z:.3f}<br>' +
        '<extra></extra>'
    }];
  }, [filteredPoints, pointSize, pointOpacity, colorScheme]);

  // Plot layout
  const plotLayout = useMemo(() => ({
    title: {
      text: '3D Point Cloud Visualization',
      font: { color: '#ffffff' }
    },
    scene: {
      xaxis: {
        title: 'X Axis',
        showgrid: showGrid,
        gridcolor: '#333333',
        zeroline: showAxes,
        zerolinecolor: '#666666'
      },
      yaxis: {
        title: 'Y Axis',
        showgrid: showGrid,
        gridcolor: '#333333',
        zeroline: showAxes,
        zerolinecolor: '#666666'
      },
      zaxis: {
        title: 'Z Axis',
        showgrid: showGrid,
        gridcolor: '#333333',
        zeroline: showAxes,
        zerolinecolor: '#666666'
      },
      camera: {
        eye: {
          x: cameraDistance * Math.cos(currentFrame * 0.01),
          y: cameraDistance * Math.sin(currentFrame * 0.01),
          z: cameraDistance * 0.5
        }
      }
    },
    paper_bgcolor: backgroundColor,
    plot_bgcolor: backgroundColor,
    font: { color: '#ffffff' },
    margin: { l: 0, r: 0, t: 50, b: 0 },
    showlegend: false
  }), [showGrid, showAxes, backgroundColor, cameraDistance, currentFrame]);

  // Animation effect
  useEffect(() => {
    if (!isAnimating || !autoRotate) return;
    
    const interval = setInterval(() => {
      setCurrentFrame(prev => prev + rotationSpeed);
    }, animationSpeed);
    
    return () => clearInterval(interval);
  }, [isAnimating, autoRotate, animationSpeed, rotationSpeed]);

  // Download data
  const handleDownload = () => {
    if (!data) return;
    
    const csvContent = [
      'x,y,z',
      ...filteredPoints.map(p => `${p.x},${p.y},${p.z}`)
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pointcloud_data.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Data</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          <Typography>No data available</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#1a1a1a', minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#ffffff', mb: 3 }}>
        3D Point Cloud Visualization Tool
      </Typography>
      
      {/* Data Source Toggle */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={useDemoData}
              onChange={(e) => setUseDemoData(e.target.checked)}
              disabled={loading}
            />
          }
          label={`Using ${useDemoData ? 'Demo' : 'Full'} Data`}
          sx={{ color: '#ffffff' }}
        />
        {useDemoData && (
          <Typography variant="body2" sx={{ color: '#cccccc', mt: 1 }}>
            Demo data contains 80 points across 8 frames. Switch to full data for the complete dataset.
          </Typography>
        )}
      </Box>
      
      <Grid container spacing={3}>
        {/* Main Visualization */}
        <Grid item xs={12} lg={9}>
          <Paper sx={{ p: 2, backgroundColor: '#2a2a2a' }}>
            <Box sx={{ position: 'relative', height: '70vh' }}>
              <Plot
                data={plotData}
                layout={plotLayout}
                style={{ width: '100%', height: '100%' }}
                config={{
                  displayModeBar: true,
                  displaylogo: false,
                  modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d']
                }}
              />
              
              {/* Overlay Controls */}
              <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                <Tooltip title="Toggle Animation">
                  <IconButton
                    onClick={() => setIsAnimating(!isAnimating)}
                    sx={{ color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.5)' }}
                  >
                    {isAnimating ? <Pause /> : <PlayArrow />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset View">
                  <IconButton
                    onClick={() => setCurrentFrame(0)}
                    sx={{ color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.5)' }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download Data">
                  <IconButton
                    onClick={handleDownload}
                    sx={{ color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.5)' }}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Control Panel */}
        <Grid item xs={12} lg={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            
            {/* Data Info */}
            <Card sx={{ backgroundColor: '#2a2a2a' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  Data Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Chip 
                    label={`Total Points: ${data?.metadata.totalPoints.toLocaleString() || 0}`}
                    sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                  />
                  <Chip 
                    label={`Displayed: ${filteredPoints.length.toLocaleString()}`}
                    sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                  />
                  <Chip 
                    label={`Frames: ${maxFrames}`}
                    sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                  />
                  <Chip 
                    label={`Data Source: ${useDemoData ? 'Demo' : 'Full'}`}
                    sx={{ backgroundColor: useDemoData ? '#2e7d32' : '#1976d2', color: '#ffffff' }}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Point Settings */}
            <Card sx={{ backgroundColor: '#2a2a2a' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  Point Settings
                </Typography>
                
                <Typography sx={{ color: '#ffffff', mb: 1 }}>Point Size</Typography>
                <Slider
                  value={pointSize}
                  onChange={(_, value) => setPointSize(value as number)}
                  min={1}
                  max={10}
                  sx={{ mb: 2 }}
                />
                
                <Typography sx={{ color: '#ffffff', mb: 1 }}>Opacity</Typography>
                <Slider
                  value={pointOpacity}
                  onChange={(_, value) => setPointOpacity(value as number)}
                  min={0.1}
                  max={1}
                  step={0.1}
                  sx={{ mb: 2 }}
                />
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel sx={{ color: '#ffffff' }}>Color Scheme</InputLabel>
                  <Select
                    value={colorScheme}
                    onChange={(e) => setColorScheme(e.target.value)}
                    sx={{ color: '#ffffff' }}
                  >
                    <MenuItem value="viridis">Viridis</MenuItem>
                    <MenuItem value="plasma">Plasma</MenuItem>
                    <MenuItem value="inferno">Inferno</MenuItem>
                    <MenuItem value="magma">Magma</MenuItem>
                    <MenuItem value="turbo">Turbo</MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>

            {/* View Settings */}
            <Card sx={{ backgroundColor: '#2a2a2a' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  View Settings
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={showGrid}
                      onChange={(e) => setShowGrid(e.target.checked)}
                    />
                  }
                  label="Show Grid"
                  sx={{ color: '#ffffff', mb: 1 }}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={showAxes}
                      onChange={(e) => setShowAxes(e.target.checked)}
                    />
                  }
                  label="Show Axes"
                  sx={{ color: '#ffffff', mb: 1 }}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoRotate}
                      onChange={(e) => setAutoRotate(e.target.checked)}
                    />
                  }
                  label="Auto Rotate"
                  sx={{ color: '#ffffff', mb: 2 }}
                />
                
                {autoRotate && (
                  <>
                    <Typography sx={{ color: '#ffffff', mb: 1 }}>Rotation Speed</Typography>
                    <Slider
                      value={rotationSpeed}
                      onChange={(_, value) => setRotationSpeed(value as number)}
                      min={0.1}
                      max={2}
                      step={0.1}
                      sx={{ mb: 2 }}
                    />
                  </>
                )}
                
                <Typography sx={{ color: '#ffffff', mb: 1 }}>Camera Distance</Typography>
                <Slider
                  value={cameraDistance}
                  onChange={(_, value) => setCameraDistance(value as number)}
                  min={1}
                  max={10}
                  sx={{ mb: 2 }}
                />
              </CardContent>
            </Card>

            {/* Data Filtering */}
            <Card sx={{ backgroundColor: '#2a2a2a' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>
                    Data Filtering
                  </Typography>
                  <IconButton
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{ color: '#ffffff', ml: 'auto' }}
                  >
                    {showFilters ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Box>
                
                {showFilters && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Point Limit"
                      type="number"
                      value={pointLimit}
                      onChange={(e) => setPointLimit(Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">points</InputAdornment>,
                      }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#ffffff' },
                        '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#666666' },
                          '&:hover fieldset': { borderColor: '#888888' },
                        }
                      }}
                    />
                    
                    <Typography sx={{ color: '#ffffff', mt: 2 }}>X Range</Typography>
                    <Slider
                      value={xRange}
                      onChange={(_, value) => setXRange(value as [number, number])}
                      min={data.metadata.bounds.x[0]}
                      max={data.metadata.bounds.x[1]}
                      valueLabelDisplay="auto"
                      sx={{ mb: 1 }}
                    />
                    
                    <Typography sx={{ color: '#ffffff' }}>Y Range</Typography>
                    <Slider
                      value={yRange}
                      onChange={(_, value) => setYRange(value as [number, number])}
                      min={data.metadata.bounds.y[0]}
                      max={data.metadata.bounds.y[1]}
                      valueLabelDisplay="auto"
                      sx={{ mb: 1 }}
                    />
                    
                    <Typography sx={{ color: '#ffffff' }}>Z Range</Typography>
                    <Slider
                      value={zRange}
                      onChange={(_, value) => setZRange(value as [number, number])}
                      min={data.metadata.bounds.z[0]}
                      max={data.metadata.bounds.z[1]}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CloudPointPage;